import React, { useState } from 'react';
import { Heart, Repeat, MessageCircle, Bookmark, Share2, ShieldCheck, Pin, MoreHorizontal } from 'lucide-react';
import { Post, User } from '../../types';
import { api } from '../../services/api';
import styles from './Post.module.css';

interface PostCardProps {
  post: Post;
  currentUser: User | null;
  onPostClick?: (post: Post) => void;
  onMediaClick?: (url: string) => void;
}

export const PostCard: React.FC<PostCardProps> = ({
  post: initialPost,
  currentUser,
  onPostClick,
  onMediaClick,
}) => {
  const [post, setPost] = useState<Post>(initialPost);

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser) return alert('Please sign in to like posts');
    try {
      const res = await api.toggleLike(post.id);
      setPost((prev) => ({
        ...prev,
        is_liked: res.isLiked,
        likes_count: res.likesCount,
      }));
    } catch (err: any) {
      alert(err.message || 'Action failed');
    }
  };

  const handleRepost = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser) return alert('Please sign in to repost');
    try {
      const res = await api.toggleRepost(post.id);
      setPost((prev) => ({
        ...prev,
        is_reposted: res.isReposted,
        reposts_count: res.repostsCount,
      }));
    } catch (err: any) {
      alert(err.message || 'Action failed');
    }
  };

  const handleBookmark = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser) return alert('Please sign in to bookmark posts');
    try {
      const res = await api.toggleBookmark(post.id);
      setPost((prev) => ({ ...prev, is_bookmarked: res.isBookmarked }));
    } catch (err: any) {
      alert(err.message || 'Action failed');
    }
  };

  const formatTimestamp = (ts: number) => {
    const diff = Math.floor((Date.now() - ts) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return new Date(ts).toLocaleDateString();
  };

  // Discord status ring color helper
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'online': return 'var(--status-online)';
      case 'idle': return 'var(--status-idle)';
      case 'dnd': return 'var(--status-dnd)';
      default: return 'var(--status-offline)';
    }
  };

  return (
    <article className={styles.postCard} onClick={() => onPostClick && onPostClick(post)}>
      {post.pinned && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: 'var(--accent-primary)', marginBottom: '8px', fontWeight: '600' }}>
          <Pin size={14} /> Pinned Post
        </div>
      )}

      <div className={styles.authorRow}>
        <div className={styles.authorInfo}>
          <div style={{ position: 'relative' }}>
            <img src={post.author.avatar_url} alt={post.author.display_name} className={styles.avatar} />
            <span
              style={{
                position: 'absolute',
                bottom: '2px',
                right: '2px',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: getStatusColor(post.author.status),
                border: '2px solid var(--bg-secondary)',
              }}
            />
          </div>
          <div>
            <div className={styles.authorName}>
              {post.author.display_name}
              {post.author.verified && <ShieldCheck size={16} color="var(--accent-primary)" fill="currentColor" />}
            </div>
            <div className={styles.username}>@{post.author.username}</div>
          </div>
        </div>

        <span className={styles.postTime}>{formatTimestamp(post.created_at)}</span>
      </div>

      <div className={styles.postContent}>{post.content}</div>

      {/* Media Attachments */}
      {post.media_urls && post.media_urls.length > 0 && (
        <div className={`${styles.mediaGrid} ${post.media_urls.length === 1 ? styles.mediaGrid1 : styles.mediaGrid2}`}>
          {post.media_urls.map((url, idx) => (
            <img
              key={idx}
              src={url}
              alt="Attachment"
              className={styles.mediaItem}
              onClick={(e) => {
                e.stopPropagation();
                onMediaClick && onMediaClick(url);
              }}
            />
          ))}
        </div>
      )}

      {/* Embedded Quote Post */}
      {post.quote_post && (
        <div className={styles.quoteEmbed}>
          <div style={{ fontWeight: '600', fontSize: '0.88rem', marginBottom: '4px' }}>
            @{post.quote_post.author.username}
          </div>
          <div style={{ fontSize: '0.9rem' }}>{post.quote_post.content}</div>
        </div>
      )}

      {/* Action Bar */}
      <div className={styles.actionBar}>
        <button className={styles.actionBtn} onClick={(e) => { e.stopPropagation(); onPostClick && onPostClick(post); }}>
          <MessageCircle size={18} />
          <span>{post.replies_count}</span>
        </button>

        <button className={`${styles.actionBtn} ${post.is_reposted ? styles.reposted : ''}`} onClick={handleRepost}>
          <Repeat size={18} />
          <span>{post.reposts_count}</span>
        </button>

        <button className={`${styles.actionBtn} ${post.is_liked ? styles.liked : ''}`} onClick={handleLike}>
          <Heart size={18} fill={post.is_liked ? 'currentColor' : 'none'} />
          <span>{post.likes_count}</span>
        </button>

        <button className={`${styles.actionBtn} ${post.is_bookmarked ? styles.bookmarked : ''}`} onClick={handleBookmark}>
          <Bookmark size={18} fill={post.is_bookmarked ? 'currentColor' : 'none'} />
        </button>

        <button className={styles.actionBtn} onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(window.location.href); alert('Link copied to clipboard!'); }}>
          <Share2 size={18} />
        </button>
      </div>
    </article>
  );
};
